import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adCampaignStates, formatMinor, minorToInput, parseMajorToMinor, type AdAccountSummary, type AdBudgetPolicy, type AdCampaignList, type AdCampaignState, type AdProviderInfo } from '@agency/contracts';
import { Badge, Button, EmptyState, Notice, Skeleton, Tabs, TextField, scrollRegion, tabId, tabPanelId, useToast } from '@agency/ui';
import { ApiClientError, get, post, put } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { QueryError } from '../../lib/query-state.tsx';
import { DemoBadge, DeliveryText, PROVIDER_LABEL, STATE, StateBadge, capabilityRows, fmtWhen, providerText } from './common.tsx';
import { OverviewTab } from './overview-tab.tsx';
import './ads.css';

type Tab = 'overview' | 'campaigns' | 'accounts';
const TABS: { id: Tab; label: string }[] = [{ id: 'overview', label: 'Overview' }, { id: 'campaigns', label: 'Campaigns' }, { id: 'accounts', label: 'Accounts and limits' }];
const VIEW_STATES: AdCampaignState[] = ['DRAFT', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'FAILED', 'OUTCOME_UNKNOWN'];

export function AdvertisingPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const tab: Tab = params.get('tab') === 'accounts' ? 'accounts' : params.get('tab') === 'campaigns' || params.get('state') ? 'campaigns' : 'overview';
  const accounts = useQuery<AdAccountSummary[], ApiClientError>({ queryKey: ['ads-accounts'], queryFn: () => get('/ads/accounts') });
  const demoInUse = accounts.data?.some((a) => a.isDemo) ?? false;
  return (
    <>
      <div className="pagehead">
        <div><h1>Advertising</h1><p>Results of live campaigns, and the place to prepare, review and publish them. Internal review state and the provider’s delivery status are tracked separately.</p></div>
        {can('ads.edit') ? <Link to="/advertising/new" className="ag-btn ag-btn--primary" style={{ textDecoration: 'none' }}>New campaign</Link> : null}
      </div>
      {demoInUse ? <div className="ads-banner"><Notice tone="info" title="Dry-run provider in use" live={false}>Campaigns on the demo account are processed by a development stand-in. Nothing is sent to Google or LinkedIn and no money is spent.</Notice></div> : null}
      <div className="ads-tabs">
        <Tabs<Tab> id="ads-tabs" label="Advertising sections" tabs={TABS} value={tab} onChange={(next) => setParams(next === 'overview' ? {} : { tab: next }, { replace: true })} />
      </div>
      <div role="tabpanel" id={tabPanelId('ads-tabs')} aria-labelledby={tabId('ads-tabs', tab)}>
        {tab === 'overview' ? <OverviewTab /> : tab === 'campaigns' ? <CampaignsTab /> : <AccountsTab accounts={accounts} />}
      </div>
    </>
  );
}

function CampaignsTab() {
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const state = adCampaignStates.find((s) => s === params.get('state'));
  const query = useInfiniteQuery<AdCampaignList, ApiClientError>({
    queryKey: ['ads-campaigns', state ?? 'all'],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const search = new URLSearchParams({ limit: '25' });
      if (state) search.set('state', state);
      if (pageParam) search.set('cursor', String(pageParam));
      return get<AdCampaignList>(`/ads/campaigns?${search}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // A campaign that is being sent to the provider settles within seconds; follow it without a manual refresh.
    refetchInterval: (q) => (q.state.data?.pages.some((p) => p.items.some((c) => c.state === 'PUBLISHING')) ? 3000 : false),
  });
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const counts = query.data?.pages[0]?.counts ?? {};
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const setState = (next?: AdCampaignState) => setParams(next ? { tab: 'campaigns', state: next } : { tab: 'campaigns' }, { replace: true });
  return (
    <>
      <div className="views ads-views" role="group" aria-label="Campaign views">
        <button type="button" className="view" aria-pressed={!state} onClick={() => setState()}>All<span className="view__n">{query.isSuccess ? total : '–'}</span></button>
        {VIEW_STATES.map((s) => (
          <button key={s} type="button" className="view" aria-pressed={state === s} onClick={() => setState(s)}>{STATE[s].label}<span className="view__n">{query.isSuccess ? counts[s] ?? 0 : '–'}</span></button>
        ))}
      </div>
      <div style={{ marginTop: 16 }}>
        {query.isLoading ? (
          <div role="status"><span className="ag-visually-hidden">Loading campaigns…</span><Skeleton width="100%" height={200} /></div>
        ) : query.isError ? (
          <QueryError error={query.error} onRetry={() => void query.refetch()} />
        ) : items.length === 0 ? (
          state ? (
            <EmptyState title={`No ${STATE[state].label.toLowerCase()} campaigns`} icon="search" action={<Button onClick={() => setState()}>Show all campaigns</Button>}>Choose another view or clear this filter.</EmptyState>
          ) : (
            <EmptyState title="No campaigns yet" icon="send" action={can('ads.edit') ? <Link to="/advertising/new" className="ag-btn ag-btn--primary" style={{ textDecoration: 'none' }}>New campaign</Link> : undefined}>
              A campaign starts from a published landing version, a connected ad account and a budget. You prepare it, an Owner approves that exact revision, then it is published.
            </EmptyState>
          )
        ) : (
          <>
            <div className="ag-table-wrap" {...scrollRegion('Campaigns table')}>
              <table className="ag-table ads-table">
                <caption className="ag-visually-hidden">Campaigns, most recently changed first</caption>
                <thead><tr><th scope="col">Campaign</th><th scope="col">State</th><th scope="col">At provider</th><th scope="col">Landing</th><th scope="col" className="ag-num">Daily budget</th><th scope="col">Revision</th><th scope="col">Updated</th></tr></thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id}>
                      <td><span className="ads-name"><Link to={`/advertising/${c.id}`}><strong>{c.name}</strong></Link><span className="ag-muted">{[c.accountName, providerText(c)].filter(Boolean).join(' · ')}</span>{c.isDemo ? <DemoBadge /> : null}</span></td>
                      <td><StateBadge state={c.state} /></td>
                      <td><DeliveryText campaign={c} /></td>
                      <td><span className="ag-cell-2"><span>{c.landingName}</span><span>v{c.landingVersionNumber}{c.marketCode ? ` · ${c.marketCode}` : ''}</span></span></td>
                      <td className="ag-num">{formatMinor(c.dailyBudgetMinor, c.currency)}</td>
                      <td className="ag-num">r{c.revisionNumber}{c.approvedRevision ? <> <Badge tone="success">Approved</Badge></> : null}</td>
                      <td title={c.updatedAt}>{fmtWhen(c.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pager"><span aria-live="polite">Showing {items.length} of {state ? counts[state] ?? 0 : total} campaign{(state ? counts[state] ?? 0 : total) === 1 ? '' : 's'}</span>{query.hasNextPage ? <Button onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>Load more</Button> : null}</div>
          </>
        )}
      </div>
    </>
  );
}

function AccountsTab({ accounts }: { accounts: ReturnType<typeof useQuery<AdAccountSummary[], ApiClientError>> }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const providers = useQuery<AdProviderInfo[], ApiClientError>({ queryKey: ['ads-providers'], queryFn: () => get('/ads/providers') });
  const demo = useMutation<AdAccountSummary, ApiClientError>({
    mutationFn: () => post('/ads/demo-connection'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['ads-accounts'] }); void qc.invalidateQueries({ queryKey: ['ads-providers'] }); void qc.invalidateQueries({ queryKey: ['ads-options'] }); toast({ tone: 'success', message: 'Demo account is connected.' }); },
    onError: (e) => toast({ tone: 'error', message: e.message }),
  });
  const dryRunOffered = providers.data?.some((p) => p.provider === 'dry-run') ?? false;
  const demoAccount = accounts.data?.find((a) => a.provider === 'dry-run');
  const realProviders = providers.data?.filter((p) => p.provider !== 'dry-run') ?? [];
  return (
    <div className="ads-stack">
      <BudgetPolicyPanel />
      <section aria-labelledby="ads-accounts-h">
        <div className="pagehead" style={{ marginBottom: 12 }}>
          <div><h2 id="ads-accounts-h">Ad accounts</h2><p>What each connection can do right now. Capabilities come from the provider adapter, not from this page.</p></div>
          {dryRunOffered && can('ads.connections.manage') && demoAccount?.connectionStatus !== 'CONNECTED' ? (
            <Button variant="primary" loading={demo.isPending} onClick={() => demo.mutate()}>{demoAccount ? 'Reconnect demo account' : 'Enable demo account'}</Button>
          ) : null}
        </div>
        {accounts.isLoading || providers.isLoading ? (
          <div role="status"><span className="ag-visually-hidden">Loading accounts…</span><Skeleton width="100%" height={160} /></div>
        ) : accounts.isError ? (
          <QueryError error={accounts.error} onRetry={() => void accounts.refetch()} />
        ) : providers.isError ? (
          <QueryError error={providers.error} onRetry={() => void providers.refetch()} />
        ) : (
          <div className="ads-cards">
            {accounts.data?.map((a) => (
              <article key={a.id} className="ads-card" aria-label={`${a.name} account`}>
                <div className="ads-card__head"><h3>{a.name}</h3><Badge tone={a.connectionStatus === 'CONNECTED' ? 'success' : a.connectionStatus === 'EXPIRED' || a.connectionStatus === 'ERROR' ? 'error' : 'warning'}>{a.connectionStatus.toLowerCase()}</Badge></div>
                {a.isDemo ? <DemoBadge /> : null}
                <dl className="ads-caps">
                  <dt>Provider</dt><dd>{PROVIDER_LABEL[a.provider]}</dd>
                  <dt>Currency</dt><dd>{a.currency}</dd>
                  <dt>Time zone</dt><dd>{a.timeZone}</dd>
                  <dt>Access</dt><dd>{a.capabilities.access.toLowerCase().replace('_', ' ')}</dd>
                  {capabilityRows(a.capabilities).map(([label, on]) => <><dt key={`${label}-t`}>{label}</dt><dd key={`${label}-d`}>{on ? 'Yes' : 'No'}</dd></>)}
                </dl>
                {a.lastError ? <Notice tone="error" title="Needs attention" live={false}>{a.lastError}</Notice> : null}
                {a.capabilities.unavailableReason ? <p className="ag-muted">{a.capabilities.unavailableReason}</p> : null}
              </article>
            ))}
            {realProviders.map((p) => p.status === 'UNCONFIGURED' && !accounts.data?.some((a) => a.provider === p.provider) ? (
              <article key={p.provider} className="ads-card" aria-label={`${p.label} setup`}>
                <div className="ads-card__head"><h3>{p.label}</h3><Badge tone="neutral">not set up</Badge></div>
                <p>{p.capabilities.unavailableReason}</p>
                <p className="ag-muted">This build has no live {p.label} adapter. Drafts for it stay blocked until one exists and an Owner connects an account.</p>
              </article>
            ) : null)}
            {!accounts.data?.length && !dryRunOffered ? <EmptyState title="No ad accounts" icon="send">No provider is available to connect in this environment.</EmptyState> : null}
          </div>
        )}
      </section>
    </div>
  );
}

function BudgetPolicyPanel() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const canEdit = can('ads.budget.manage');
  const policy = useQuery<AdBudgetPolicy | null, ApiClientError>({ queryKey: ['ads-budget-policy'], queryFn: () => get('/ads/budget-policy') });
  const [currency, setCurrency] = useState('EUR');
  const [daily, setDaily] = useState('');
  const [monthly, setMonthly] = useState('');
  const [confirm, setConfirm] = useState('');
  useEffect(() => {
    const p = policy.data;
    if (!p) return;
    setCurrency(p.currency);
    setDaily(minorToInput(p.maxDailyCampaignMinor, p.currency));
    setMonthly(minorToInput(p.maxMonthlyAccountMinor, p.currency));
    setConfirm(minorToInput(p.confirmationThresholdMinor, p.currency));
  }, [policy.data]);
  const cur = currency.trim().toUpperCase();
  const dailyMinor = parseMajorToMinor(daily, cur);
  const monthlyMinor = parseMajorToMinor(monthly, cur);
  const confirmMinor = parseMajorToMinor(confirm, cur);
  const problems = {
    currency: /^[A-Z]{3}$/.test(cur) ? '' : 'Use a three-letter currency code such as EUR.',
    daily: dailyMinor === null || dailyMinor < 1 ? 'Enter an amount greater than zero.' : '',
    monthly: monthlyMinor === null || monthlyMinor < 1 ? 'Enter an amount greater than zero.' : '',
    confirm: confirmMinor === null || confirmMinor < 1 ? 'Enter an amount greater than zero.' : dailyMinor !== null && confirmMinor > dailyMinor ? 'The threshold cannot be above the daily limit.' : '',
  };
  const valid = !problems.currency && !problems.daily && !problems.monthly && !problems.confirm;
  const save = useMutation<AdBudgetPolicy, ApiClientError>({
    mutationFn: () => put('/ads/budget-policy', { currency: cur, maxDailyCampaignMinor: dailyMinor, maxMonthlyAccountMinor: monthlyMinor, confirmationThresholdMinor: confirmMinor }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['ads-budget-policy'] }); void qc.invalidateQueries({ queryKey: ['ads-options'] }); void qc.invalidateQueries({ queryKey: ['ads-campaign'] }); toast({ tone: 'success', message: 'Budget limits saved.' }); },
    onError: (e) => toast({ tone: 'error', message: e.message }),
  });
  return (
    <section className="ads-card" aria-labelledby="ads-policy-h">
      <h2 id="ads-policy-h">Budget limits</h2>
      <p className="ag-muted">Campaigns cannot be submitted or published without limits. The 30-day figure is a planning ceiling (daily budget × 30 across approved and live campaigns), not provider-billed spend.</p>
      {policy.isLoading ? <div role="status"><span className="ag-visually-hidden">Loading budget limits…</span><Skeleton height={90} /></div> : policy.isError ? <QueryError error={policy.error} onRetry={() => void policy.refetch()} /> : (
        <>
          {!policy.data ? <Notice tone="warning" title="No budget limits are set" live={false}>{canEdit ? 'Set the limits below to allow campaigns to be submitted and published.' : 'Ask an Owner to set budget limits. Until then campaigns cannot be submitted or published.'}</Notice> : null}
          {canEdit ? (
            <form className="ads-two" onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }} noValidate>
              <TextField label="Currency" value={currency} maxLength={3} error={problems.currency && currency ? problems.currency : undefined} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
              <TextField label="Daily limit per campaign" inputMode="decimal" value={daily} error={daily && problems.daily ? problems.daily : undefined} onChange={(e) => setDaily(e.target.value)} />
              <TextField label="30-day limit per account" inputMode="decimal" value={monthly} error={monthly && problems.monthly ? problems.monthly : undefined} onChange={(e) => setMonthly(e.target.value)} />
              <TextField label="Confirm amounts from" hint="At or above this daily budget the publisher must retype the amount." inputMode="decimal" value={confirm} error={confirm && problems.confirm ? problems.confirm : undefined} onChange={(e) => setConfirm(e.target.value)} />
              <div style={{ alignSelf: 'end' }}><Button variant="primary" type="submit" disabled={!valid} loading={save.isPending}>Save limits</Button></div>
            </form>
          ) : policy.data ? (
            <dl className="ads-facts">
              <dt>Daily limit per campaign</dt><dd>{formatMinor(policy.data.maxDailyCampaignMinor, policy.data.currency)}</dd>
              <dt>30-day limit per account</dt><dd>{formatMinor(policy.data.maxMonthlyAccountMinor, policy.data.currency)}</dd>
              <dt>Confirm amounts from</dt><dd>{formatMinor(policy.data.confirmationThresholdMinor, policy.data.currency)} per day</dd>
              {policy.data.updatedAt ? <><dt>Last changed</dt><dd>{fmtWhen(policy.data.updatedAt)}</dd></> : null}
            </dl>
          ) : null}
        </>
      )}
    </section>
  );
}
